import { type Activity, getApiConfig, type Portfolio } from '@netpurpose/api'
import type { TablePaginationConfig } from '@netpurpose/types'

const PAGE_SIZE = 50

const featureToggles = {
  incompleteDisclosureIcon: 'client-app.disclosure-incomplete-icon',
  portfolioTimeSeries: 'client-app.portfolio-time-series',
  recentPortfolios: 'client-app.recent-portfolios',
  globalSearchAskAi: 'client-app.global-search-ask-ai',
} as const

const routes = {
  landingPage: '/',
  login: '/login',
  explore: '/explore',
  benchmark: '/benchmark',
  portfolioList: '/portfolios',
  portfolioCreation: '/portfolios/create',
  library: '/library',
  goals: '/goals',
  forgotPassword: '/forgot-password',
  register: '/register',
  signup: '/sign-up',
  enrollMultifactor: '/enroll-mfa',
  metricConfigs: '/metric-configs',
} as const

const dynamicRoutes = {
  portfolioDetail: (portfolioId: Portfolio['id'], activeTab?: string | undefined) =>
    `${routes.portfolioList}/${portfolioId}${activeTab ? `?active-tab=${activeTab}` : ''}`,
  portfolioDetailForSnapshot: (portfolioId: Portfolio['id'], snapshotId: Portfolio['snapshotId']) =>
    `${routes.portfolioList}/${portfolioId}?snapshotId=${snapshotId}`,
  portfolioDetailMetricConfigs: (portfolioId: Portfolio['id'], fromDisclosure?: boolean) =>
    `${routes.portfolioList}/${portfolioId}/metric-configs${fromDisclosure ? '?from_tab=disclosure' : '?from_tab=impact'}`,
  portfolioEntityDetail: (portfolioId: Portfolio['id'], entityId: number) =>
    `${routes.portfolioList}/${portfolioId}/${entityId}`,
  portfolioEntityDetailMetricConfigs: (portfolioId: Portfolio['id'], entityId: number) =>
    `${routes.portfolioList}/${portfolioId}/${entityId}/metric-configs`,
  portfolioFundDetail: (
    parentPortfolioId: number,
    portfolioId: number,
    snapshotId?: number | null,
  ) =>
    `${routes.portfolioList}/fof/${parentPortfolioId}/${portfolioId}${snapshotId ? `?snapshotId=${snapshotId}` : ''}`,
  portfolioFundEntityDetail: (parentPortfolioId: number, portfolioId: number, entityId: number) =>
    `${routes.portfolioList}/fof/${parentPortfolioId}/${portfolioId}/${entityId}`,
  entityDetail: (entityId: number) => `${routes.explore}/${entityId}`,
  entityDetailMetricConfigs: (entityId: number) => `${routes.explore}/${entityId}/metric-configs`,
  entityDataDetail: (entityId: number, theme?: string) =>
    `${dynamicRoutes.entityDetail(entityId)}?active-tab=data${theme ? `&theme=${theme}` : ''}`,
  estimationDetail: (entityId: number, techIds: Activity['name'][], portfolioId?: number) =>
    `${dynamicRoutes.entityDetail(entityId)}/estimation?techIds=${encodeURIComponent(techIds.join('-'))}${portfolioId ? `&portfolioId=${portfolioId}` : ''}`,
  metricConfig: (metricConfigId: string, portfolioId?: number) =>
    `${routes.metricConfigs}/${metricConfigId}${portfolioId ? `?portfolio_id=${portfolioId}` : ''}`,
  domainDetail: (taxonId: number) => `${routes.explore}/taxonomy/domain/${taxonId}`,
  solutionDetail: (taxonId: number) => `${routes.explore}/taxonomy/solution/${taxonId}`,
  baselineDetail: (taxonId: number) => `${routes.explore}/taxonomy/baseline/${taxonId}`,
  questionDetail: (questionId: number, theme?: string) => {
    const query = new URLSearchParams({ 'active-tab': 'data', questionId: `${questionId}` })
    if (theme) {
      query.set('theme', theme)
    }
    return `${routes.explore}?${query.toString()}`
  },
} as const

export type RouteName = keyof typeof routes

const localStorageKeys = {
  dataView: 'netpurpose:application:selectedDataView',
  recentGlobalSearchResults: (groupId: string) =>
    `netpurpose:application:recentGlobalSearchResults:${groupId}`,
  lastViewedPortfolios: (groupId: string) =>
    `netpurpose:application:lastViewedPortfolios:${groupId}`,
  latestReleaseClicked: 'netpurpose:application:latestReleaseClicked',
} as const

const appConfig = {
  packageName: process.env['NEXT_PUBLIC_NP_APP_NAME'] || '',
  deploymentEnv: process.env['NEXT_PUBLIC_DEPLOYMENT_ENV'] || '',
  localStorageKeys,
  routes,
  dynamicRoutes,
  unprotectedRoutes: ['login', 'forgotPassword', 'register', 'signup'] satisfies RouteName[],
  taskResultsPollDelay: 5000,
  locale: 'en-GB',
  defaultCountry: 'GB', // Two-letter country code - https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
  defaultFetchSize: PAGE_SIZE,
  defaultTablePagination: {
    showSizeChanger: false,
    hideOnSinglePage: true,
    pageSize: PAGE_SIZE,
  } satisfies TablePaginationConfig,
  featureToggles,
  privacyPolicyUrl: 'https://app.privasee.io/privacy-portal/646cc22bda2352001473b13e',
  pipeDriveApiBaseUrl: 'https://api.pipedrive.com/v1',
} as const

export const config = { ...getApiConfig(), ...appConfig }
